import { categories } from './categories.js'

/**
 * Parsar en enskild ingrediensrad till { amount, unit, name }
 * T.ex:
 * "2.5 dl vetemjöl" -> { amount: 2.5, unit: 'dl', name: 'vetemjöl' }
 * "3 st ägg" -> { amount: 3, unit: 'st', name: 'ägg' }
 * "Smör till stekning" -> { amount: null, unit: '', name: 'Smör till stekning' }
 */
export function parseIngredient(line) {
  const trimmed = line.trim().replace(/^[-*•]\s*/, '')
  if (!trimmed) return null

  // Kända enheter i svenska recept
  const knownUnits = '(?:dl|cl|ml|l|liter|g|gram|kg|kilo|tsk|msk|krm|st|stycken|klyftor|klyfta|kruka|krukor|burk|burkar|nypa|paket|pkt|förp)'
  const unitRegex = new RegExp(`^([0-9]+(?:[.,][0-9]+)?(?:/[0-9]+)?)\\s*(${knownUnits})?\\s*(.*)$`, 'i')

  const match = trimmed.match(unitRegex)
  if (match && match[1]) {
    let amountStr = match[1].replace(',', '.')
    let amount = parseFloat(amountStr)
    if (amountStr.includes('/')) {
      const [num, den] = amountStr.split('/')
      amount = parseFloat(num) / parseFloat(den)
    }

    const unit = match[2] ? match[2].toLowerCase() : 'st'
    const name = match[3] ? match[3].trim() : ''

    return {
      amount,
      unit,
      name: name || unit
    }
  }

  // Om raden bara startar med siffra och namn, t.ex. "3 ägg"
  const numberFirst = trimmed.match(/^([0-9]+(?:[.,][0-9]+)?)\s+(.+)$/)
  if (numberFirst) {
    return {
      amount: parseFloat(numberFirst[1].replace(',', '.')),
      unit: 'st',
      name: numberFirst[2].trim()
    }
  }

  // Ingrediens utan mängdangivelse, t.ex. "Smör till stekning", "Salt och peppar"
  return {
    amount: null,
    unit: '',
    name: trimmed
  }
}

/**
 * Skapar ett slug-id från en titel, t.ex. "Klassiska tunna pannkakor" -> "klassiska-tunna-pannkakor"
 */
function createSlug(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[åä]/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Standardbilder baserade på kategori om ingen bild anges
 */
const defaultImages = {
  pannkakor: 'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?auto=format&fit=crop&w=800&q=80',
  indiskt: 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?auto=format&fit=crop&w=800&q=80',
  default: 'https://images.unsplash.com/photo-1495521821757-a1efb6729352?auto=format&fit=crop&w=800&q=80'
}

/**
 * Huvudfunktion: Omvandlar en helt vanlig text till ett fullständigt receptobjekt
 */
export function skapaRecept(text, overrides = {}) {
  const lines = text.split('\n')
  
  const meta = {}
  const ingredients = []
  const instructions = []
  let currentSection = 'meta'

  for (let rawLine of lines) {
    const line = rawLine.trim()
    if (!line) continue

    // Kolla sektionsrubriker
    const lower = line.toLowerCase()
    if (lower.startsWith('ingredienser:') || lower === 'ingredienser') {
      currentSection = 'ingredients'
      continue
    }
    if (lower.startsWith('instruktioner:') || lower.startsWith('gör så här:') || lower === 'instruktioner' || lower === 'gör så här') {
      currentSection = 'instructions'
      continue
    }
    if (lower.startsWith('tips:') || lower === 'tips') {
      const tipsContent = line.replace(/^tips:\s*/i, '').trim()
      if (tipsContent) {
        meta.tips = tipsContent
      }
      currentSection = 'tips'
      continue
    }

    if (currentSection === 'meta') {
      // Parsar Nyckel: Värde
      const colonIdx = line.indexOf(':')
      if (colonIdx > 0) {
        const key = line.slice(0, colonIdx).trim().toLowerCase()
        const val = line.slice(colonIdx + 1).trim()

        if (key === 'titel' || key === 'namn' || key === 'title') {
          meta.title = val
        } else if (key === 'kategori' || key === 'category') {
          meta.category = val.toLowerCase()
        } else if (key === 'beskrivning' || key === 'description') {
          meta.description = val
        } else if (key === 'tid' || key === 'total tid' || key === 'totaltid') {
          meta.totalTime = val
        } else if (key === 'förberedelse' || key === 'preptime' || key === 'prep') {
          meta.prepTime = val
        } else if (key === 'tillagning' || key === 'cooktime' || key === 'koktid' || key === 'stektid') {
          meta.cookTime = val
        } else if (key === 'portioner' || key === 'servings') {
          const num = parseInt(val, 10)
          meta.servings = isNaN(num) ? 4 : num
        } else if (key === 'svårighetsgrad' || key === 'difficulty') {
          meta.difficulty = val
        } else if (key === 'bild' || key === 'image' || key === 'bildurl') {
          meta.imageUrl = val
        } else if (key === 'tips') {
          meta.tips = val
        }
      }
    } else if (currentSection === 'ingredients') {
      const parsed = parseIngredient(line)
      if (parsed) {
        ingredients.push(parsed)
      }
    } else if (currentSection === 'instructions') {
      // Ta bort inledande numrering som 1., 1), etc.
      const stepText = line.replace(/^[0-9]+[.)]\s*/, '').trim()
      if (stepText) {
        instructions.push(stepText)
      }
    } else if (currentSection === 'tips') {
      if (!meta.tips) {
        meta.tips = line
      } else {
        meta.tips += ' ' + line
      }
    }
  }

  // Hitta kategoridata (namn och ikon)
  const categoryId = meta.category || 'alla'
  const matchedCategory = categories.find(c => c.id === categoryId)

  const title = meta.title || 'Namnlöst recept'
  const id = meta.id || createSlug(title)

  return {
    id,
    title,
    category: categoryId,
    categoryName: matchedCategory ? matchedCategory.name : (meta.category ? meta.category.charAt(0).toUpperCase() + meta.category.slice(1) : 'Recept'),
    categoryIcon: matchedCategory ? matchedCategory.icon : '🍽️',
    description: meta.description || '',
    prepTime: meta.prepTime || '',
    cookTime: meta.cookTime || '',
    totalTime: meta.totalTime || meta.cookTime || meta.prepTime || '30 min',
    servings: meta.servings || 4,
    difficulty: meta.difficulty || 'Lätt',
    imageUrl: meta.imageUrl || defaultImages[categoryId] || defaultImages.default,
    ingredients,
    instructions,
    tips: meta.tips || '',
    ...overrides
  }
}
